import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import groupService from '../services/GroupService';
import { debugError } from '../utils/debugUtils';

const TOP_N = 25;

function MedalIcon({ rank }) {
  if (rank === 1) return <span style={{ fontSize: '1.1rem' }}>🥇</span>;
  if (rank === 2) return <span style={{ fontSize: '1.1rem' }}>🥈</span>;
  if (rank === 3) return <span style={{ fontSize: '1.1rem' }}>🥉</span>;
  return (
    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, minWidth: 20, textAlign: 'center' }}>
      {rank}
    </Typography>
  );
}

/**
 * Tabla de clasificación definitiva de puntos del día para un grupo concreto.
 * Muestra el top 25 del grupo y, si el usuario autenticado no está entre ellos, añade su fila al final.
 *
 * @param {number} groupId    - Id del grupo
 * @param {string} groupName  - Nombre del grupo (para el título)
 * @param {string} date       - Fecha en formato "YYYY-MM-DD" (periodKey del ranking diario)
 * @param {object} user       - Usuario actual de authService.getUser() ({ name, email })
 */
export default function GroupDailyRanking({ groupId, groupName, date, user }) {
  const { t } = useTranslation();
  const [topEntries, setTopEntries] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    if (!date || !groupId) return;
    setTopEntries(null);
    setCurrentUser(null);
    groupService
      .getRanking(groupId, 'DAILY', { periodKey: date, limit: TOP_N })
      .then((data) => {
        setTopEntries(Array.isArray(data?.topEntries) ? data.topEntries : []);
        setCurrentUser(data?.currentUser ?? null);
      })
      .catch((err) => {
        debugError('[GroupDailyRanking] Error:', err);
        setTopEntries([]);
      });
  }, [groupId, date]);

  if (topEntries === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (topEntries.length === 0) return null;

  const userName = user ? (user.name && user.name.trim() ? user.name : user.email) : null;

  const highlightSx = {
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
    borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
    '& td': { fontWeight: 700 },
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        {t('results.groupRankingTitle', { groupName })}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, width: 40, textAlign: 'center' }}>#</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t('leaderboard.player')}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>{t('leaderboard.total')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {topEntries.map((p) => (
            <TableRow key={p.rank} sx={p.name === userName ? highlightSx : {}}>
              <TableCell sx={{ textAlign: 'center' }}>
                <MedalIcon rank={p.rank} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={p.name === userName ? 700 : 400} component="span">
                  {p.name}
                </Typography>
                {p.name === userName && (
                  <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 0.5 }}>
                    ({t('results.yourWordBadge')})
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight={700} color={p.rank <= 3 ? 'success.main' : 'text.primary'}>
                  {p.totalScore} pts
                </Typography>
              </TableCell>
            </TableRow>
          ))}

          {currentUser && (
            <>
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 0.25, border: 0 }}>
                  <Typography variant="caption" color="text.disabled">···</Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={highlightSx}>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    {currentUser.rank}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={700} component="span">
                    {currentUser.name}
                  </Typography>
                  <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 0.5 }}>
                    ({t('results.yourWordBadge')})
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700} color="text.secondary">
                    {currentUser.totalScore} pts
                  </Typography>
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
